import { isString, isNumber, countBy } from 'lodash-es';
import { nanoid } from 'nanoid';
import type { CoreTypes } from '../../../core';
import type { Types } from '../types';
import type { FromInterface } from '../../../common/types';
import { makeChainable } from '../../../common/make-chainable';

type C_Settings = Pick<CoreTypes.Serialized.Settings, 'discretization' | 'numDays' | 'dayStart' | 'dayEnd'>;
type C_Location = Omit<CoreTypes.Serialized.Location, '_id'> & { __raw: Types.Location  };
type C_Group    = Omit<CoreTypes.Serialized.Group,    '_id'> & { __raw: Types.Group     };
type C_Teacher  = Omit<CoreTypes.Serialized.Teacher,  '_id'> & { __raw: Types.Teacher   };
type C_Course   = Omit<CoreTypes.Serialized.Course,   '_id'> & { __raw: Types.Course    };
type C_Person   = Omit<CoreTypes.Serialized.Person,   '_id'> & { __raw: Types.Person    };
type C_Event    = Omit<CoreTypes.Serialized.Event,    '_id'> & { __raw?: Types.Course   };
type C_Syllabus = Omit<CoreTypes.Serialized.Syllabus, '_id'> & { __raw?: Types.Syllabus };
type C_LinkedEventsSet = Omit<CoreTypes.Serialized.LinkedEventsSet, '_id'> & { __raw?: Types.Course };

type Out = {
  settings:   C_Settings;
  locations:  C_Location[];
  teachers:   C_Teacher[];
  persons:    C_Person[];
  groups:     C_Group[];
  courses:    C_Course[];
  events:     C_Event[];
  syllabuses: C_Syllabus[];
  linkedEventsSets: C_LinkedEventsSet[];
  meta: {
    // errors?:   string[];
    warnings?: { code: Types.ErrorCode, context?: unknown }[];
  };
};


function generateId (map: Map<string, unknown>) {
  // generate a unique id
  let id: string;
  do {
    id = nanoid(8);
  } while (map.has(id));

  return id;
}

function toPositiveMultipleOf5 (val: number | string) {
  const num = isString(val) ? parseInt(val) : val;

  // throw error if nan
  if (isNaN(num)) throw new Error(`toMultipleOf5: ${val} is not a number`);

  const res = Math.floor(num / 5) * 5;

  if (res <= 0) throw new Error(`toMultipleOf5: the result ${res} is not positive`);

  return res;
}

function toPositiveInteger (val: number | string) {
  const num = isString(val) ? parseInt(val) : val;

  // throw error if nan
  if (isNaN(num)) throw new Error(`toInteger: ${val} is not a number`);
  if (num <= 0) throw new Error(`toInteger: te result ${num} is not positive`);

  return num;
}

/**
 * Converts a string/number to a trimmed string
 */
function toString (x: string | number): string {
  return isNumber(x)
    ? x.toString()
    : x.trim();
}

/**
 * Converts a possible nullish string/number to a trimmed string or undefined
 */
function toStringIfExists (x: string | number | undefined | null): string | undefined {
  if (x == null) return undefined;
  return toString(x);
}

function toNumberIfExists (x: string | number | undefined | null): number | undefined {
  if (x == null) return undefined;
  const num = isString(x) ? parseInt(x) : x;
  if (isNaN(num)) return undefined;
  return num;
}

function extractLocations (
  course:           Types.Course,
  locationsNameMap: Map<string, C_Location>,
  warnings:         { code: Types.ErrorCode, context?: unknown }[],
) {
  if (course.locations == null) return;
  const locationsStr = toString(course.locations);
  if (!locationsStr) return;

  const locationReferences: { groupIndex: number; to: string }[] = [];

  locationsStr.split('+')
    .flatMap((x, i) => x.split(',').map(x => [i, x.trim()] as [number, string]))
    .forEach(([i, x]) => {
      const key = x.toLowerCase();
      const ref = locationsNameMap.get(key);
      if (!ref) {
        warnings.push({ code: 'invalid_location_reference', context: x });
        return;
      }

      if (locationReferences.some(y => y.to == ref.id)) {
        warnings.push({ code: 'location_referenced_multiple_times', context: x });
        return;
      }

      locationReferences.push({ groupIndex: i, to: ref.id });
    });

  return locationReferences;
}

function extractGroups (
  course:         Types.Course,
  groupsNameMap:  Map<string, C_Group>,
  warnings:       { code: Types.ErrorCode, context?: unknown }[],
) {
  if (course.groups == null) return;
  const groupsStr = toString(course.groups);
  if (!groupsStr) return;

  const groupReferences: { to: string }[] = [];
  groupsStr.split(',').map(x => x.trim())
    .forEach(x => {
      const key = x.toLowerCase();
      const ref = groupsNameMap.get(key);
      if (!ref) {
        warnings.push({ code: 'invalid_group_reference', context: x });
        return;
      }

      if (groupReferences.some(y => y.to == ref.id)) {
        warnings.push({ code: 'group_referenced_multiple_times', context: x });
        return;
      }

      groupReferences.push({ to: ref.id });
    });

  return groupReferences;
}

function extractTeachers (
  course:           Types.Course,
  teachersNameMap:  Map<string, C_Teacher>,
  teachersSignatureMap: Map<string, C_Teacher>,
  warnings:         { code: Types.ErrorCode, context?: unknown }[],
) {
  if (course.teachers == null) return;
  const teachersStr = toString(course.teachers);
  if (!teachersStr) return undefined;

  const teacherReferences: { to: string }[] = [];
  teachersStr.split(',').map(x => x.trim())
    .forEach(x => {
      const key = x.toLowerCase();
      const refName = teachersNameMap.get(key);
      const refSignature = teachersSignatureMap.get(key);

      const ref = refName || refSignature;

      if (!ref) {
        warnings.push({ code: 'invalid_teacher_reference', context: x });
        return;
      }

      if (teacherReferences.some(y => y.to == ref.id)) {
        warnings.push({ code: 'teacher_referenced_multiple_times', context: x });
        return;
      }

      teacherReferences.push({ to: ref.id });
    });

  return teacherReferences;
}

function extractSyllabus (
  course: Types.Course,
  syllabusesCodeMap: Map<string, C_Syllabus>,
  syllabusesNameMap: Map<string, C_Syllabus>,
  warnings: { code: Types.ErrorCode, context?: unknown }[],
): string | undefined {
  if (course.syllabus == null) return undefined;
  const syllabusValue = toString(course.syllabus);
  if (!syllabusValue) return undefined;

  const syllabus = syllabusesCodeMap.get(syllabusValue) || syllabusesNameMap.get(syllabusValue.toLowerCase());

  if (!syllabus) {
    warnings.push({ code: 'invalid_syllabus_reference', context: syllabusValue });
    return undefined;
  }

  return syllabus.id;
}

const PREFERRED_SEQUENTIAL_LENGTH = 2;

function getSetsFromCourses<T extends { events?: C_Event[] }>(courses: T[]): NonNullable<T['events']>[number][][] {
  const coursesWithEvents = courses
    .map(c => c.events ?? [])
    .filter(events => events.length > 0);

  // If there are less than 2 courses we can't form parallel sets
  if (coursesWithEvents.length < 2) return [];

  const numSets = Math.min(...coursesWithEvents.map(events => events.length));

  // Sort each course's events by effective duration
  const sortedCourses = coursesWithEvents.map(events =>
    [...events].sort((a, b) => a.preferredDuration - b.preferredDuration)
  );

  // Step 1: Prune excess events
  // Compute target durations from courses that already have exactly numSets events
  const referenceCourses = sortedCourses.filter(events => events.length === numSets);
  const targets = Array.from({ length: numSets }, (_, i) => {
    const durations = referenceCourses.map(events => events[i].preferredDuration).sort((a, b) => a - b);
    return durations[Math.floor(durations.length / 2)]; // median
  });

  const prunedCourses = sortedCourses.map(events => {
    if (events.length === numSets) return events;
    return selectBestSubset(events, targets);
  });

  // Step 2: Build sets by sorted-rank alignment — events at the same rank
  // across courses share similar durations
  const sets: C_Event[][] = [];
  for (let i = 0; i < numSets; i++) {
    sets.push(prunedCourses.map(events => events[i]));
  }
  return sets;
}

/**
 * Picks `targets.length` events from a sorted event list to minimise total
 * absolute deviation from the target durations.  Uses O(n·k) DP.
 */
function selectBestSubset (events: C_Event[], targets: number[]): C_Event[] {
  const n = events.length;
  const k = targets.length;

  // dp[i][j] = min cost picking j events from the first i (1-indexed)
  const dp: number[][] = Array.from({ length: n + 1 }, () => Array(k + 1).fill(Infinity));
  for (let i = 0; i <= n; i++) dp[i][0] = 0;

  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= Math.min(i, k); j++) {
      // skip event i
      dp[i][j] = dp[i - 1][j];
      // take event i as j-th pick
      const cost = Math.abs(events[i - 1].preferredDuration - targets[j - 1]);
      if (dp[i - 1][j - 1] + cost < dp[i][j]) {
        dp[i][j] = dp[i - 1][j - 1] + cost;
      }
    }
  }

  // Back-track to recover selected events
  const selected: C_Event[] = [];
  let j = k;
  for (let i = n; i >= 1 && j > 0; i--) {
    const cost = Math.abs(events[i - 1].preferredDuration - targets[j - 1]);
    if (dp[i][j] === dp[i - 1][j - 1] + cost) {
      selected.unshift(events[i - 1]);
      j--;
    }
  }

  return selected;
}


function _schedules (
  source: Types.Source
): Out {
  // gather all import related warnings/errors
  const warnings: { code: Types.ErrorCode, context?: unknown }[] = [];
  // let errors:   string[] = [];

  source.settings  = source.settings ?.filter(x => Object.values(x).some(y => toString(y).length > 0));
  source.locations = source.locations?.filter(x => Object.values(x).some(y => toString(y).length > 0));
  source.teachers  = source.teachers ?.filter(x => Object.values(x).some(y => toString(y).length > 0));
  source.groups    = source.groups   ?.filter(x => Object.values(x).some(y => toString(y).length > 0));
  source.persons   = source.persons  ?.filter(x => Object.values(x).some(y => toString(y).length > 0));
  source.courses   = source.courses  ?.filter(x => Object.values(x).some(y => toString(y).length > 0));


  ////
  //// settings
  ////
  const settings: C_Settings = {
    discretization: 5,
    numDays:        5,
    dayStart:       '08:00',
    dayEnd:         '17:00',
  };
  if (!source.settings) {
    warnings.push({ code: 'missing_settings_sheet' });
  }
  if (source.settings?.[0]?.number_of_days) {
    const numDays = source.settings?.[0]?.number_of_days;
    try {
      settings.numDays = toPositiveInteger(numDays);

      // must be 5 or 7
      if (settings.numDays !== 5 && settings.numDays !== 7) {
        warnings.push({ code: 'invalid_number_of_days', context: numDays });
        settings.numDays = 5;
      }
    } catch {
      warnings.push({ code: 'invalid_number_of_days', context: numDays });
    }
  }

  ////
  //// syllabuses
  ////
  const syllabusesIdMap = new Map<string, C_Syllabus>();
  const syllabusesNameMap = new Map<string, C_Syllabus>();
  const syllabusesCodeMap = new Map<string, C_Syllabus>();
  if (!source.syllabuses) {
    // warnings.push({ code: 'missing_syllabus_sheet' });
  }
  source.syllabuses?.forEach(x => {
    const ids         = toStringIfExists(x.foreign_ID  );
    const subjectCode = toStringIfExists(x.subject_code);
    const subjectName = toStringIfExists(x.subject_name);
    const courseCode  = toStringIfExists(x.course_code );
    const courseName  = toStringIfExists(x.course_name );
    const schoolType  = toStringIfExists(x.school_type );

    const id = toStringIfExists(x.ID) ?? generateId(syllabusesIdMap);
    if (syllabusesIdMap.has(id)) {
      warnings.push({ code: 'ignoring_duplicate_syllabus', context: `${subjectName} (${courseName})` });
      return;
    }

    if (!subjectName) {
      warnings.push({ code: 'missing_subject_name', context: x });
      return;
    }

    if (!schoolType) {
      warnings.push({ code: 'missing_school_type', context: x });
      return;
    }

    // create the syllabus object
    const y: C_Syllabus = {
      id,
      ...ids             && { ids             },
      ...subjectCode     && { subjectCode     },
      subjectName,
      ...courseCode      && { courseCode      },
      ...courseName      && { courseName      },
      schoolType,
      official: true, // always true for imported syllabuses
      __raw:    x
    };
    syllabusesIdMap.set(id, y);
    const code = courseCode || subjectCode;
    const name = courseName || subjectName;
    if (code) syllabusesCodeMap.set(code, y);
    if (name) syllabusesNameMap.set(name.toLowerCase(), y);
  });


  ////
  //// locations, teachers
  ////
  const locationsIdMap       = new Map<string, C_Location>();
  const locationsNameMap     = new Map<string, C_Location>();
  const teachersIdMap        = new Map<string, C_Teacher>();
  const teachersNameMap      = new Map<string, C_Teacher>();
  const teachersSignatureMap = new Map<string, C_Teacher>();
  [
    {
      name:    { singular: 'location', plural: 'locations' } as const,
      data:    source.locations,
      idMap:   locationsIdMap,
      nameMap: locationsNameMap,
      signatureMap: undefined,
    },
    {
      name:    { singular: 'teacher', plural: 'teachers' } as const,
      data:    source.teachers,
      idMap:   teachersIdMap,
      nameMap: teachersNameMap,
      signatureMap: teachersSignatureMap,
    }
  ].forEach(({ name, data, idMap, nameMap, signatureMap }) => {
    if (!data) {
      warnings.push({ code: `missing_${name.plural}_sheet` });
    }
    data?.forEach(x => {
      const ids         = toStringIfExists(x.foreign_ID);
      const displayName = toStringIfExists(x.name      );

      const id = toStringIfExists(x.ID) ?? generateId(idMap);
      if (idMap.has(id)) {
        warnings.push({ code: `ignoring_duplicate_${name.singular}`, context: displayName });
        return;
      }

      const y = {
        id,
        ...ids         && { ids },
        ...displayName && { displayName },
        __raw: x
      };
      idMap.set(id, y);
      if (displayName) nameMap.set(displayName.toLowerCase(), y);
      if (signatureMap && 'signature' in x && x.signature) signatureMap.set((x as C_Teacher).signature!.toLowerCase(), y);
    });
  });


  ////
  //// groups
  ////
  const groupsIdMap   = new Map<string, C_Group>();
  const groupsNameMap = new Map<string, C_Group>();
  if (!source.groups) {
    warnings.push({ code: 'missing_groups_sheet' });
  }
  source.groups?.forEach(x => {
    const ids         = toStringIfExists(x.foreign_ID);
    const displayName = toStringIfExists(x.name      );

    const id = toStringIfExists(x.ID) ?? generateId(groupsIdMap);
    if (groupsIdMap.has(id)) {
      warnings.push({ code: 'ignoring_duplicate_group', context: displayName });
      return;
    }

    // parent groups determine the species of the group
    const species = toStringIfExists(x.is_class) == '1' ? 'class' as const : undefined;

    const y = {
      id,
      ...ids         && { ids },
      ...displayName && { displayName },
      ...species     && { species },
      __raw: x
    } as C_Group;
    groupsIdMap.set(id, y);
    if (displayName) groupsNameMap.set(displayName.toLowerCase(), y);
  });


  ////
  //// persons
  ////
  const personsIdMap  = new Map<string, C_Person>();
  const personsSsnMap = new Map<string, C_Person>();
  if (!source.persons) {
    warnings.push({ code: 'missing_persons_sheet' });
  }
  source.persons?.forEach(x => {
    const ids       = toStringIfExists(x.foreign_ID);
    const firstName = toStringIfExists(x.first_name);
    const lastName  = toStringIfExists(x.last_name );
    const SSN       = toStringIfExists(x.SSN       );

    const id = toStringIfExists(x.ID) ?? generateId(personsIdMap);
    if (personsIdMap.has(id)) {
      warnings.push({ code: 'ignoring_duplicate_person', context: `${firstName} ${lastName}` });
      return;
    }

    let group: string | undefined = undefined;
    if (x.group) {
      const key = toString(x.group).toLowerCase();
      const ref = groupsNameMap.get(key);
      if (!ref) {
        warnings.push({ code: 'invalid_group_reference', context: x.group });
      } else if (ref.species !== 'class') {
        warnings.push({ code: 'forbidden_group_reference', context: x.group });
      } else {
        group = ref.id;
      }
    }

    let sex: C_Person['sex'];
    if (x.sex) {
      if      (isString(x.sex) && x.sex.toLowerCase() == 'man')   sex = 'Man';
      else if (isString(x.sex) && x.sex.toLowerCase() == 'woman') sex = 'Woman';
      else {
        warnings.push({ code: 'invalid_sex', context: x.sex });
      }
    }

    const person: C_Person = {
      id,
      ...ids       && { ids },
      ...firstName && { firstName },
      ...lastName  && { lastName },
      ...sex       && { sex },
      ...group     && { group },
      ...SSN       && { SSN: { value: SSN } },
      __raw: x
    };
    personsIdMap.set(id, person);
    if (SSN) personsSsnMap.set(SSN, person);
  });

  ////
  //// group.members (need to wait for persons to be parsed)
  ////
  groupsNameMap.forEach(x => {
    let duplicateReported = false;

    // ignore if no members
    if (!x.__raw.members) return;

    // parse and replace SSN by person id
    x.members = makeChainable(x.__raw.members)
      .chain(x => toString(x)
        .split(',')
        .map(x => x.trim())
        .filter(x => x != null)
      )
      .chain(x => countBy(x))
      .chain(y => Object.entries(y)
        .map(([ssn, count]) => {
          // ignore empty ssn
          if (!ssn) return;

          // warn if duplicate member
          if (!duplicateReported && count > 1) {
            warnings.push({ code: 'duplicate_group_member', context: x.displayName });
            duplicateReported = true;
          }

          // fetch person and warn if not found
          const person = personsSsnMap.get(ssn);
          if (!person) {
            warnings.push({ code: 'invalid_person_reference', context: ssn });
            return;
          }

          // warn if the group is a class and the person is already in a different class
          // (we also need to set the group of the person if it is empty)
          if (x.species == 'class') {
            if (!person.group) {
              person.group = x.id;
            }
            else if (person.group != x.id) {
              warnings.push({ code: 'person_in_multiple_classes', context: ssn });
              return;
            }
          }

          return person.id;
        })
        .filter(x => x != null)
      )
      .value;
  });


  ////
  //// courses
  ////
  const coursesIdMap = new Map<string, C_Course      >();
  const eventsIdMap  = new Map<string, C_Event       >();
  const linkedEventsParallelIdMap = new Map<string, C_LinkedEventsSet>();
  const linkedEventsSequentialIdMap = new Map<string, C_LinkedEventsSet>();
  // A map for storing lessons that we later will make parallel links between.
  const parallelLessonsMap = new Map<string, C_Event[]>();

  if (!source.courses) {
    warnings.push({ code: 'missing_courses_sheet' });
  }
  source.courses?.forEach(x => {
    // await loading events until courses are created
    const mergeWith = toStringIfExists(x.merge_with);
    if (mergeWith) return;

    const ids         = toStringIfExists(x.foreign_ID);
    const displayName = toStringIfExists(x.name      );
    const subject     = toStringIfExists(x.subject   );

    const courseId = toStringIfExists(x.ID) ?? generateId(coursesIdMap);
    if (coursesIdMap.has(courseId)) {
      warnings.push({ code: 'ignoring_duplicate_course', context: displayName + (subject ? ` (${subject})` : '') });
      return;
    }

    // try to parse the total hours as an integer
    let expectedTotalHours: number | undefined = undefined;
    if (x.total_hours) {
      try {
        expectedTotalHours = toPositiveInteger(x.total_hours);
      } catch {
        warnings.push({ code: 'invalid_total_hours', context: x.total_hours });
      }
    }

    // try to parse the minutes per week as an multiple of 5
    let minutesPerWeek: number | undefined = undefined;
    if (x.minutes_per_week) {
      try {
        minutesPerWeek = toPositiveMultipleOf5(x.minutes_per_week);
      } catch {
        warnings.push({ code: 'invalid_minutes_per_week', context: x.minutes_per_week });
      }
    }

    // only one of minutes_per_week or total_hours should be defined
    if (minutesPerWeek != null && expectedTotalHours != null) {
      warnings.push({ code: 'ambiguous_planned_duration', context: `${minutesPerWeek} min/week OR ${expectedTotalHours} hrs` });
    }
    const plannedDuration =
      expectedTotalHours
        ? `${expectedTotalHours} hrs` :
        minutesPerWeek
          ? `${minutesPerWeek} min/week` :
          undefined;


    // try to parse the lesson durations as an array of integers from which we will generate events
    if (x.lesson_durations) {
      const _durations = typeof x.lesson_durations === 'string'
        ? x.lesson_durations.split(',').map(x => x.trim())
        : [x.lesson_durations];

      // truncate to multiple of 5 and filter out zero duration events
      const durations = _durations
        .map(e => {
          try {
            return toPositiveMultipleOf5(e);
          } catch {
            warnings.push({ code: 'invalid_lesson_durations', context: x.lesson_durations });
            return undefined;
          }
        })
        .filter((x): x is number => x !== undefined);

      let queuedSequentialEvent: C_Event | null = null;
      let createdSequentialEventsCount = 0;

      durations.forEach(y => {
        const id = generateId(eventsIdMap);
        const event: C_Event = { id, course: courseId, preferredDuration: y };


        const sequentialCount = toNumberIfExists(x.linked_lessons_sequential);
        const parallelIdentifier = toStringIfExists(x.linked_lessons_parallel);

        if (sequentialCount && parallelIdentifier) {
          warnings.push({ code: 'both_linked_lessons_fields_present', context: x });
        } else {
          if (parallelIdentifier) {
            // Just set the event in the parallel lessons map for now, we will create the linked events sets at the end when we have all the events (we need to wait until we have all the events to be able to link all the events with the same parallel identifier, even if they are not in the same course)
            if (!parallelLessonsMap.has(parallelIdentifier)) {
              parallelLessonsMap.set(parallelIdentifier, []);
            }
            parallelLessonsMap.get(parallelIdentifier)!.push(event);
          }

          if (sequentialCount && (createdSequentialEventsCount < sequentialCount)) {
            if (!queuedSequentialEvent) {
              queuedSequentialEvent = event;
            } else {
              // We now have a pair of sequential events, so we create a linked events set for them
              const linkedEventsSetId = generateId(linkedEventsSequentialIdMap);
              const linkedEventsSet: C_LinkedEventsSet = {
                id: linkedEventsSetId,
                type: 'sequential',
                gap: { min: 0, max: 10 }, // Default gap
                events: [queuedSequentialEvent.id, event.id],
                __raw: x
              };
              linkedEventsSequentialIdMap.set(linkedEventsSetId, linkedEventsSet);
              queuedSequentialEvent = null;
              createdSequentialEventsCount++;
            }
          }
        }

        eventsIdMap.set(id, event);
      });
    }

    const locations = extractLocations(x, locationsNameMap, warnings)?.map(x => ({ groupIndex: x.groupIndex, locations: [x.to] as [string] }));
    const groups    = extractGroups   (x, groupsNameMap,    warnings);
    const teachers  = extractTeachers (x, teachersNameMap, teachersSignatureMap,  warnings);
    const syllabus  = extractSyllabus(x, syllabusesCodeMap, syllabusesNameMap, warnings);

    const course: C_Course = {
      id:    courseId,
      ...ids                && { ids                },
      ...displayName        && { displayName        },
      ...subject            && { subject            },
      /* TO BE DEPRECATED */ ...expectedTotalHours && { expectedTotalHours },
      /* TO BE DEPRECATED */ ...minutesPerWeek     && { minutesPerWeek     },
      ...plannedDuration    && { plannedDuration    },
      ...groups             && { groups             },
      ...teachers           && { teachers           },
      ...locations          && { locations          },
      ...syllabus           && { syllabus           },
      __raw: x
    };
    coursesIdMap.set(courseId, course);
  });



  ////
  //// events
  ////
  source.courses?.forEach(x => {
    // load courses
    const mergeWith = toStringIfExists(x.merge_with);
    if (!mergeWith) return;

    // ensure that the course to merge with exists
    if (!coursesIdMap.has(mergeWith)) {
      warnings.push({ code: 'invalid_course_reference', context: mergeWith });
      return;
    }

    const displayName = toStringIfExists(x.name);
    const locations   = extractLocations(x, locationsNameMap, warnings)?.map(x => ({ groupIndex: x.groupIndex, locations: [x.to] as [string] }));
    const groups      = extractGroups   (x, groupsNameMap,    warnings);
    const teachers    = extractTeachers (x, teachersNameMap, teachersSignatureMap, warnings);

    // try to parse the lesson durations as an array of integers from which we will generate events
    if (x.lesson_durations) {
      const _durations = typeof x.lesson_durations === 'string'
        ? x.lesson_durations.split(',').map(x => x.trim())
        : [x.lesson_durations];

      // truncate to multiple of 5 and filter out zero duration events
      const durations = _durations
        .map(e => {
          try {
            return toPositiveMultipleOf5(e);
          } catch {
            warnings.push({ code: 'invalid_lesson_durations', context: x.lesson_durations });
            return undefined;
          }
        })
        .filter((x): x is number => x !== undefined);

      durations.forEach(preferredDuration => {
        const id = generateId(eventsIdMap);
        const event: C_Event = {
          id,
          course:            mergeWith,
          preferredDuration: preferredDuration,
          ...displayName  && { displayName },
          ...groups       && { groups      },
          ...teachers     && { teachers    },
          ...locations    && { locations   },
          __raw:             x
        };
        eventsIdMap.set(id, event);
      });
    }
  });

  ////
  //// Linked events (parallel)
  ////
  parallelLessonsMap.values().forEach((events) => {
    if (events.length < 2) return; // No need to create a linked events set if there is only one event

    // Sort into courses
    const eventsByCourse = new Map<string, C_Event[]>();
    events.forEach(event => {
      const courseId = event.course!;
      if (!eventsByCourse.has(courseId)) {
        eventsByCourse.set(courseId, []);
      }
      eventsByCourse.get(courseId)!.push(event);
    });

    if (eventsByCourse.size < 2) return; // We can't create a linked events set if there is only one course involved

    const courses = Array.from(eventsByCourse.values()).map(events => ({ events }));
    const sets = getSetsFromCourses(courses);

    sets.forEach(set => {
      const linkedEventsSetId = generateId(linkedEventsParallelIdMap);
      const linkedEventsSet: C_LinkedEventsSet = {
        id: linkedEventsSetId,
        type: 'parallel',
        events: set.map(e => e.id),
        anchor: 'middle', // Default anchor
      };
      linkedEventsParallelIdMap.set(linkedEventsSetId, linkedEventsSet);
    });
  });


  ////
  //// return
  ////
  return {
    settings,
    locations:  [...locationsIdMap .values()],
    teachers:   [...teachersIdMap  .values()],
    persons:    [...personsIdMap   .values()],
    groups:     [...groupsIdMap    .values()],
    courses:    [...coursesIdMap   .values()],
    events:     [...eventsIdMap    .values()],
    syllabuses: [...syllabusesIdMap.values()],
    linkedEventsSets: [...linkedEventsParallelIdMap.values(), ...linkedEventsSequentialIdMap.values()],
    meta:       {
      // ...errors  .length && { errors:   errors   },
      ...warnings.length && { warnings: warnings }
    }
  };
}


export default {
  schedules: _schedules
} satisfies FromInterface;